/* eslint-disable no-case-declarations */
/**
 * @file MyLabsView.jsx
 * @author Fernando Ruano
 * @description My Labs View Page
 */

// Dependencies
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Collapse, Divider } from 'antd';
import { CloseCircleFilled, CheckCircleFilled } from '@ant-design/icons';
import styled from 'styled-components';
import moment from 'moment';

// Components
import AppCardLayout from '@nephro/components/AppCardLayout';
import LabsCard from '@nephro/components/LabsCard';

// Helpers
import {
  LÍPIDOS_INDICATOR,
  PTH_INDICATOR,
  ALBUMIN_INDICATOR,
  HEMOGLOBINA_INDICATOR,
  PHOSPHORUS_INDICATOR,
  POTASIO_INDICATOR,
  STAGE_INDICATOR,
  COLESTEROL_PARAM,
  TRIGLICERIDOS_PARAM,
  CREATININE_INDICATOR
} from '@nephro/pages/Indicators/helpers/constants';
import usePatientIndicators from '@nephro/pages/Indicators/helpers/usePatientIndicators';
import useIndicatorParams from '@nephro/pages/Indicators/helpers/useIndicatorParams';
import { getPatientStage } from '@nephro/pages/Indicators/helpers/utils';
import { getStorageItem } from 'commons/helpers/storage-helper';

const { Panel } = Collapse;

const StyledPanel = styled(Panel)`
  background-color: white;
  border: solid 1px #bac3d4;
  font-weight: 500;
  color: var(--color-baxter-gray);
  font-size: 16px;
`;

function MyLabsViewView({ patientActions }) {
  const { getPthPatient } = patientActions;
  const hemoglobinaIndicator = usePatientIndicators(HEMOGLOBINA_INDICATOR);
  const albuminIndicator = usePatientIndicators(ALBUMIN_INDICATOR);
  const potasioIndicator = usePatientIndicators(POTASIO_INDICATOR);
  const phosphorusIndicator = usePatientIndicators(PHOSPHORUS_INDICATOR);
  const pthIndicator = usePatientIndicators(PTH_INDICATOR);
  const [PTHApplay, setPTHApplay] = useState(null);
  const colesterolParam = useIndicatorParams(
    LÍPIDOS_INDICATOR,
    COLESTEROL_PARAM
  );
  const trigliceridosParam = useIndicatorParams(
    LÍPIDOS_INDICATOR,
    TRIGLICERIDOS_PARAM
  );
  const creatinineIndicator = usePatientIndicators(CREATININE_INDICATOR);
  const [stage] = usePatientIndicators(STAGE_INDICATOR);
  const { t: translate } = useTranslation();

  const stageValue = getPatientStage(stage?.valor);

  const applies = translate('indicators.myLabs.applies');
  const notApplies = translate('indicators.myLabs.notApplies');
  const low = translate('indicators.myLabs.low');
  const hign = translate('indicators.myLabs.high');
  const critic = translate('indicators.myLabs.critic');

  const notAppliesIcon = (
    <CloseCircleFilled
      style={{
        color: 'white',
        fontSize: 20
      }}
    />
  );

  const appliesIcon = (
    <CheckCircleFilled
      style={{
        color: 'var(--color-secondary)',
        fontSize: 20
      }}
    />
  );

  const appliesStyle = {
    text: applies,
    type: 'normal',
    icon: appliesIcon
  };

  const notAppliesStyle = {
    text: notApplies,
    type: 'danger',
    icon: notAppliesIcon
  };

  const getPTHApplay = (patientInfo) => {
    const data = {
      document: patientInfo.id_document,
      documentTypeId: patientInfo.identification_type.id_versia,
      result: 1
    };
    getPthPatient(data, (_, response) => {
      if (response?.data) {
        const PTHApplayStyle =
          response.data === 'No Cumple' ? notAppliesStyle : appliesStyle;
        setPTHApplay(PTHApplayStyle);
      }
    });
  };

  useEffect(async () => {
    const patientInfo = await getStorageItem('patientInfo');
    if (patientInfo && pthIndicator && pthIndicator.length)
      getPTHApplay(patientInfo);
  }, [pthIndicator]);

  const labsArray = [
    {
      header: 'Creatinina',
      childs: creatinineIndicator
    },
    {
      header: 'Hemoglobina',
      childs: hemoglobinaIndicator
    },
    {
      header: 'Albumina',
      childs: albuminIndicator
    },
    {
      header: 'Potasio',
      childs: potasioIndicator
    },
    {
      header: 'Fósforo',
      childs: phosphorusIndicator
    },
    {
      header: 'PTH',
      childs: pthIndicator
    }
  ];

  const lipidosArray = [
    { title: 'Colesterol LDL', childs: colesterolParam },
    { title: 'Triglicéridos', childs: trigliceridosParam }
  ];

  const onGetStatusByLab = (itm) => {
    const value = itm?.valor;
    switch (itm.indicador_id) {
      case CREATININE_INDICATOR: // Creatinina validation
        return { text: '', type: 'normal' };
      case HEMOGLOBINA_INDICATOR: // Hemoglobina Validation
        return value < 10 ? notAppliesStyle : appliesStyle;

      case ALBUMIN_INDICATOR: // Albumin Validation
        let albuminStatus = { text: 'Normal', type: 'normal' };

        if (value < 4) albuminStatus = { text: low, type: 'warning' };
        else if (value < 3) albuminStatus = { text: critic, type: 'danger' };
        return albuminStatus;

      case POTASIO_INDICATOR: // Potasio Validation
        let potasioStatus = { text: 'Normal', type: 'normal' };

        if (value > 6) potasioStatus = { text: critic, type: 'danger' };
        else if (value > 5.1 && value <= 6)
          potasioStatus = { text: hign, type: 'danger' };
        else if (value < 3.5) potasioStatus = { text: low, type: 'danger' };
        return potasioStatus;

      case PHOSPHORUS_INDICATOR: // Potasio Validation
        let phosphorusStatus = { text: 'Normal', type: 'normal' };

        if (value > 6) phosphorusStatus = { text: critic, type: 'danger' };
        else if (value > 4.5 && value <= 6)
          phosphorusStatus = { text: hign, type: 'warning' };
        else if (value < 2.8) phosphorusStatus = { text: low, type: 'warning' };
        return phosphorusStatus;

      case PTH_INDICATOR: // PTH Validation
        let pthStatus = notAppliesStyle;
        const normalPthStatus = appliesStyle;

        if ((stageValue === '3a' || stageValue === '3b') && value <= 70)
          pthStatus = normalPthStatus;
        else if (stageValue === '4' && value <= 110)
          pthStatus = normalPthStatus;
        else if (stageValue === '5' && value <= 300)
          pthStatus = normalPthStatus;
        return pthStatus;

      default:
        return {};
    }
  };

  const onGetStatusByLipidosParam = (itm) => {
    const value = itm?.valor;

    switch (itm.parametro) {
      case COLESTEROL_PARAM: // PTH Validation
        return value >= 100 ? notAppliesStyle : appliesStyle;

      case TRIGLICERIDOS_PARAM: // Albumin Validation
        return value >= 150 ? notAppliesStyle : appliesStyle;

      default:
        return {};
    }
  };

  const getUnities = (indicator) => {
    switch (indicator) {
      case POTASIO_INDICATOR:
        return 'mEq/L';
      case CREATININE_INDICATOR:
      case PHOSPHORUS_INDICATOR:
        return 'mg/dL';
      case PTH_INDICATOR:
        return 'pg/mL';
      default:
        return 'gr/dL';
    }
  };

  return (
    <AppCardLayout>
      <div
        style={{ fontSize: 18 }}
        className="text-center p-5 items-center m-auto text-baxterGray mt-1">
        <p className="text-left mb-8">{translate('indicators.myLabs.title')}</p>
        {stageValue && (
          <section className="mb-10 justify-between w-full text-left">
            <Collapse style={{ border: 'solid 0px #bac3d4' }} accordion>
              {labsArray?.map((itm, idx) => {
                return (
                  <StyledPanel
                    style={{
                      borderTopRightRadius: idx === 0 ? 10 : 0,
                      borderTopLeftRadius: idx === 0 ? 10 : 0,
                      borderBottom: 0
                    }}
                    key={idx}
                    header={itm.header}>
                    <div className="w-full flex">
                      <section className="flex flex-wrap mb-10 w-full justify-center gap-2 sm:gap-x-36">
                        {itm?.childs?.map((itmChild, idxChild) => {
                          const isPTHPrevious =
                            idxChild === 1 &&
                            itmChild.indicador_id === PTH_INDICATOR;
                          const unities = getUnities(itmChild.indicador_id);
                          const PTHStyles = isPTHPrevious
                            ? PTHApplay
                            : onGetStatusByLab(itmChild);
                          return (
                            <LabsCard
                              key={idxChild}
                              title={
                                idxChild === 0
                                  ? translate('indicators.last')
                                  : translate('indicators.previous')
                              }
                              subtitle={moment
                                .utc(itmChild?.fecha)
                                .format('DD/MM/YYYY')}
                              value={itmChild.valor}
                              unities={unities}
                              footerIcon={PTHStyles?.icon}
                              footerText={PTHStyles?.text}
                              type={PTHStyles?.type}
                            />
                          );
                        })}
                      </section>
                    </div>
                  </StyledPanel>
                );
              })}
              <StyledPanel
                style={{
                  borderBottomRightRadius: 10,
                  borderBottomLeftRadius: 10,
                  borderBottom: 'solid 1px #bac3d4'
                }}
                header="Lípidos">
                {lipidosArray?.map((itm, idx) => {
                  return (
                    <div key={idx}>
                      {itm?.childs?.length > 0 && <h3>{itm.title}</h3>}
                      <div className="w-full flex justify-center">
                        <div className="flex flex-row justify-between w-full md:max-w-1/2">
                          {itm?.childs?.map((itmChild, idxChild) => {
                            const status = onGetStatusByLipidosParam(itmChild);
                            return (
                              <LabsCard
                                key={idxChild}
                                title={
                                  idxChild === 0
                                    ? translate('indicators.last')
                                    : translate('indicators.previous')
                                }
                                subtitle={moment
                                  .utc(itmChild?.fecha)
                                  .format('DD/MM/YYYY')}
                                value={itmChild.valor}
                                unities="mg/dL"
                                footerIcon={status.icon}
                                footerText={status.text}
                                type={status.type}
                              />
                            );
                          })}
                        </div>
                      </div>
                      {itm?.childs?.length > 0 &&
                        idx !== itm?.childs?.length - 1 && (
                          <Divider
                            style={{
                              backgroundColor: '#bac3d4',
                              marginTop: 10,
                              marginBottom: 10
                            }}
                          />
                        )}
                    </div>
                  );
                })}
              </StyledPanel>
            </Collapse>
          </section>
        )}
      </div>
    </AppCardLayout>
  );
}

/**
 * Component definition of proptypes.
 */
MyLabsViewView.propTypes = {
  patientActions: PropTypes.shape({
    getPthPatient: PropTypes.func.isRequired
  }).isRequired
};

export default MyLabsViewView;
